import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React from 'react';
import { required, Validators } from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';

import styles from './assets/credit-form.module.scss';
import { faSave, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { asyncPersistCredit } from 'src/store/actions/credit-actions';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from 'src/components/redux-form/fields/FieldDate';

import { Paginated } from 'src/services/api-handlers/pagination';
import InvoicePaymentGrid from '../../Payments/components/components/InvoicePaymentGrid';
import { IServiceType } from '../../../../../types/service-types';
import { IInvoice } from '../../../../../types/invoice';
import { IPayment } from '../../../../../types/payment';
import { numberWithCommas } from '../../../../../helpers/format';

export interface CreditFormData {
    id: string | number;
    creditInvoices: any;
    creditCredits: any;
    amount: number;
    payment: Partial<IPayment>;
    caseType: number;
    eftInfo: string;
    applianceDate: string;
}

interface CreditFormProps {
    students: SelectObject[];
    vendors: SelectObject[];
    districts: SelectObject[];
    providers: SelectObject[];
    invoices: Paginated<IInvoice>;

    stateData: any;
    creditCredits?: any;
    remainAmount: number;
    amountValue: number;
    totalAmount: number;

    caseTypeHistory?: any;
    setStateData: (data: any) => void;
    getData: (data: any) => void;
    serviceTypes: Paginated<IServiceType>;
    handleSub: (data: (e: any) => void, e: any) => void;
}

type InjectedProps = InjectedFormProps<CreditFormData, CreditFormProps>;

const validators: Validators<CreditFormData> = {
    applianceDate: [required({ message: 'Appliance Date is required.' })],
    eftInfo: [required({ message: 'Check/EFT Info is required.' })],
    amount: [required({ message: 'Amount, $ is required.' })],
};

const CreditForm: React.FunctionComponent<InjectedProps & CreditFormProps> = ({
    handleSubmit,
    error,
    students,
    vendors,
    districts,
    invoices,
    providers,
    serviceTypes,
    dirty,
    setStateData,
    getData,
    stateData,
    submitting,
    remainAmount,
    totalAmount,
    amountValue,
    handleSub,
    initialValues,
}: InjectedProps & CreditFormProps) => {
    const isNew = !initialValues || !initialValues.id;

    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <FormError error={error} />

            <div className="d-flex flex-row">
                <div className="w-25 mr-5">
                    <FieldDate
                        name="applianceDate"
                        type="text"
                        validate={validators.applianceDate}
                        date={true}
                        wrapperConfig={{
                            label: 'Appliance Date: *',
                        }}
                    />
                </div>
                <div className="w-25 mr-5">
                    <FieldInput
                        name="eftInfo"
                        validate={validators.eftInfo}
                        type="text"
                        disabled={true}
                        wrapperConfig={{
                            label: 'Check/EFT Info: *',
                        }}
                    />
                </div>
                <div className="w-25 mr-5">
                    <FieldInput
                        name="amount"
                        validate={validators.amount}
                        type="number"
                        disabled={true}
                        wrapperConfig={{
                            label: 'Credit Amount, $: *',
                        }}
                    />
                </div>
                <div className="w-25 mr-5">
                    <FieldInput
                        name="caseNumber"
                        type="text"
                        disabled={true}
                        wrapperConfig={{
                            label: 'Case Number:',
                        }}
                    />
                </div>
            </div>
            <div className={'list-invoices'}>
                <div className={`${classNames('pageHeadingTitle')}`}>Apply to Cases & Invoices:</div>
                <InvoicePaymentGrid
                    students={students}
                    vendors={vendors}
                    invoices={invoices}
                    districts={districts}
                    providers={providers}
                    getData={getData}
                    caseTypeHistory={{}}
                    serviceTypes={serviceTypes}
                    stateData={stateData}
                    amountValue={amountValue}
                    isCredit={true}
                    caseType={initialValues?.caseType}
                    paymentId={initialValues?.payment?.id}
                    setStateData={setStateData}
                    changeValue={(field, val) => false}
                />
            </div>
            <div className={'credits'}>
                <div className={`${classNames('pageHeadingTitle')}`}>
                    Sum of the entered values: ${numberWithCommas(totalAmount)}. Remaining amount:{' '}
                    <span> ${numberWithCommas(remainAmount)}</span>
                </div>
            </div>

            <div className={classNames(styles.actions)}>
                <button
                    type="button"
                    onClick={(e) => {
                        handleSub(handleSubmit, e);
                    }}
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> {isNew ? 'Create' : 'Update'}
                </button>
                <Link className={classNames('btn', 'btn-danger')} to={Router.generate(routes.CREDITS_LIST)}>
                    <FontAwesomeIcon icon={faBan} /> Cancel
                </Link>
            </div>
        </form>
    );
};

export default reduxForm<CreditFormData, CreditFormProps>({
    form: 'credit-form',
    enableReinitialize: true,
    onSubmit: async (data: CreditFormData, dispatch: Dispatch<any>, props: CreditFormProps) => {
        await asyncPersistCredit(
            { ...data, caseType: +data.caseType, creditInvoices: props.stateData },
            //props.files,
            dispatch,
        );
    },

    onSubmitSuccess(result: any, dispatch: Dispatch<any>, props: DecoratedFormProps<CreditFormData, CreditFormProps>) {
        const isNew = !props.initialValues || !props.initialValues.id;

        toast.success(`Credit was successfully ${isNew ? 'created' : 'applied'}.`);
    },

    onSubmitFail(errors: { _error: string }) {
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(CreditForm);
